import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useCreateReason,
  usePriorityLevel,
  useUpdateReason,
} from "@features/cx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface TransferReason {
  id: number;
  configurationCode: string;
  name: string;
  longDescription: string;
  extraData: {
    acknowledgementTime: number;
    responseTime: number;
    resolutionTime: number;
    priorityLevel: number;
    requiredApproval: boolean;
  };
}

interface TransferReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type?: TransferReason | null;
  onClose: () => void;
  onSave?: (reasonData: TransferReason) => void;
}

export function TransferTypeDialog({
  open,
  onOpenChange,
  type,
  onClose,
  onSave,
}: TransferReasonDialogProps) {
  const createTransferReason = useCreateReason();
  const updateTransferReason = useUpdateReason();
  const { data: priorityData } = usePriorityLevel();
  const remoteUtilityId = useRemoteUtilityId();
  const [formData, setFormData] = useState({
    code: type?.configurationCode || "",
    name: type?.name || "",
    longDescription: type?.longDescription || "",
    acknowledgementTime: type?.extraData.acknowledgementTime,
    responseTime: type?.extraData.responseTime,
    resolutionTime: type?.extraData.resolutionTime,
    priority: type?.extraData.priorityLevel,
    requiresApproval: type?.extraData.requiredApproval,
  });

  useEffect(() => {
    setFormData({
      code: type?.configurationCode || "",
      name: type?.name || "",
      longDescription: type?.longDescription || "",
      acknowledgementTime: type?.extraData.acknowledgementTime,
      responseTime: type?.extraData.responseTime,
      resolutionTime: type?.extraData.resolutionTime,
      priority: type?.extraData.priorityLevel,
      requiresApproval: type?.extraData.requiredApproval,
    });
  }, [type, open]);

  const getPriorityOptions = useMemo(() => {
    if (!priorityData || !Array.isArray(priorityData)) {
      return [];
    }
    return priorityData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [priorityData]);

  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Transfer Type Name",
      placeholder: "e.g., Non-payment",
      required: true,
      className: "col-span-1",
      maxLength: 40,
    },
    {
      name: "code",
      type: "text",
      label: "Transfer Code",
      placeholder: "e.g., TRANSFER",
      required: true,
      className: "col-span-1",
    },
    {
      name: "longDescription",
      type: "textarea",
      label: "Description",
      placeholder: "Describe when this reason should be used...",
      rows: 3,
      className: "col-span-2",
    },
    {
      name: "acknowledgementTime",
      type: "number",
      label: "Acknowledgement Time (Hours)",
      placeholder: "24",
      min: 1,
      required: true,
      className: "col-span-1",
    },
    {
      name: "responseTime",
      type: "number",
      label: "Response Time (Hours)",
      placeholder: "48",
      min: 1,
      required: true,
      className: "col-span-1",
    },
    {
      name: "resolutionTime",
      type: "number",
      label: "Resolution Time (Hours)",
      placeholder: "72",
      min: 1,
      required: true,
      className: "col-span-1",
    },
    {
      name: "priority",
      type: "select",
      label: "Priority",
      placeholder: "Select priority level",
      required: true,
      options: getPriorityOptions,
      className: "col-span-1",
    },
    {
      name: "requiresApproval",
      type: "switch",
      label: "Requires Approval",
      helperText: "Whether this reason requires manager approval",
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = () => {
    const operation = type ? "update" : "create";
    switch (operation) {
      case "update":
        const payloadEdit = {
          id: type?.id,
          name: formData.name,
          configuration_code: formData.code,
          long_description: formData.longDescription,
          request_type: "Transfer",
          remote_utility_id: Number(remoteUtilityId),
          acknowledgement_time: Number(formData.acknowledgementTime),
          response_time: Number(formData.responseTime),
          resolution_time: Number(formData.resolutionTime),
          priority_level: formData.priority,
          required_approval: formData.requiresApproval,
        };

        updateTransferReason.mutateAsync(payloadEdit);
        break;

      case "create":
        const payloadAdd = {
          name: formData.name,
          configuration_code: formData.code,
          long_description: formData.longDescription,
          request_type: "Transfer",
          remote_utility_id: Number(remoteUtilityId),
          acknowledgement_time: Number(formData.acknowledgementTime),
          response_time: Number(formData.responseTime),
          resolution_time: Number(formData.resolutionTime),
          priority_level: formData.priority,
          required_approval: formData.requiresApproval,
        };

        createTransferReason.mutateAsync(payloadAdd);
        break;
    }

    handleClose();
  };
  // Handle form reset
  const handleReset = () => {
    const resetData = {
      code: "",
      name: "",
      longDescription: "",
      acknowledgementTime: "",
      responseTime: "",
      resolutionTime: "",
      priority: "",
      requiresApproval: false,
    };
    setFormData(resetData);
    form.reset(resetData);
  };

  // Handle dialog close
  const handleClose = () => {
    handleReset();
    onClose();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {type ? "Edit Transfer Type" : "Add New Transfer Type"}
          </DialogTitle>
          <DialogDescription>
            Configure the type, timing requirements, and priority settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: type ? "Update Type" : "Add Type",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
